/**
 * ETAP 6 – Baza wiedzy.
 * Jedno źródło treści dla /baza-wiedzy i podstron artykułów.
 * Każdy artykuł ma unikalne metadane, dokładnie jeden H1 (title) i linki
 * do podstron produktowych oraz zastosowań branżowych.
 */
export type KnowledgeSection = {
  h2: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type KnowledgeLink = { label: string; href: string; text: string };

export type KnowledgeArticle = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  date: string;
  category: string;
  lead: string;
  sections: KnowledgeSection[];
  faqs: { q: string; a: string }[];
  links: KnowledgeLink[];
  related: string[];
};

const L = {
  wypukle: {
    label: "Naklejki wypukłe 3D",
    href: "/naklejki-wypukle-3d",
    text: "Nadruk zalany żywicą poliuretanową – podstawowy produkt w naszej ofercie.",
  },
  plaskie: {
    label: "Naklejki płaskie",
    href: "/naklejki-plaskie",
    text: "Wersja bez żywicy, gdy liczy się przede wszystkim cena i duży nakład.",
  },
  identyfikatory: {
    label: "Identyfikatory 3D",
    href: "/identyfikatory-3d",
    text: "Tabliczki i oznaczenia urządzeń z wypukłą warstwą żywicy.",
  },
  zalewanie: {
    label: "Zalewanie żywicą na materiale klienta",
    href: "/zalewanie-zywica-na-materiale-klienta",
    text: "Usługa domingu na Twoim gotowym nadruku.",
  },
  branze: {
    label: "Zastosowania branżowe naklejek 3D",
    href: "/zastosowania-branzowe",
    text: "Przykłady wykorzystania naklejek 3D w konkretnych branżach.",
  },
  kalkulator: {
    label: "Kalkulator wyceny",
    href: "/kalkulator",
    text: "Podaj wymiary, nakład i folię, aby zobaczyć cenę od razu.",
  },
  realizacje: {
    label: "Przykładowe realizacje",
    href: "/realizacje",
    text: "Filmy pokazujące, jak wyglądają gotowe naklejki 3D.",
  },
} satisfies Record<string, KnowledgeLink>;

export const KNOWLEDGE_ARTICLES: KnowledgeArticle[] = [
  {
    slug: "jak-przygotowac-plik-do-druku-naklejek-3d",
    title: "Jak przygotować plik do druku naklejek 3D",
    metaTitle: "Jak przygotować plik do druku naklejek 3D – wymagania",
    metaDescription:
      "Wymagania techniczne plików do naklejek 3D: format wektorowy, 300 DPI, spady, kolory CMYK i Pantone, linia cięcia. Poradnik dla firm i grafików.",
    excerpt:
      "Format pliku, rozdzielczość, spady, linia cięcia i kolory – wszystko, co musi zawierać projekt, aby produkcja przebiegła bez poprawek.",
    date: "2026-09-10",
    category: "Projekt i pliki",
    lead:
      "Dobrze przygotowany plik skraca produkcję i eliminuje poprawki. Poniżej opisujemy, w jakiej formie przyjmujemy projekty naklejek 3D i na co zwrócić uwagę przy przygotowaniu grafiki.",
    sections: [
      {
        h2: "Najlepszy format pliku",
        paragraphs: [
          "Najbezpieczniejszy jest plik wektorowy: PDF, AI, EPS lub SVG. Wektor można skalować bez utraty jakości, a krzywe posłużą jednocześnie jako linia cięcia naklejki.",
          "Jeśli masz wyłącznie grafikę rastrową, przyjmiemy plik PNG, TIFF lub JPG – pod warunkiem, że jego rozdzielczość w docelowym rozmiarze naklejki wynosi minimum 300 DPI.",
        ],
        bullets: [
          "Wektor: PDF, AI, EPS, SVG – zalecany.",
          "Raster: PNG, TIFF, JPG – minimum 300 DPI w rozmiarze docelowym.",
          "Czcionki zamienione na krzywe lub dołączone do pliku.",
        ],
      },
      {
        h2: "Rozmiar, spady i linia cięcia",
        paragraphs: [
          "Projekt przygotuj w skali 1:1, w rozmiarze gotowej naklejki. Realizujemy naklejki od 5 × 5 mm do 380 × 280 mm.",
          "Zalecamy około 2 mm spadu wokół grafiki oraz zachowanie 2–3 mm marginesu bezpieczeństwa dla ważnych elementów – tekstu, logotypu i cienkich linii. Linia cięcia powinna być oddzielną ścieżką w pliku, najlepiej na osobnej warstwie.",
        ],
      },
      {
        h2: "Kolory i biały poddruk",
        paragraphs: [
          "Grafikę przygotuj w CMYK. Kolory firmowe podaj dodatkowo jako Pantone – dzięki temu możemy je odwzorować możliwie dokładnie. Pamiętaj, że kolory z monitora (RGB) zawsze wyglądają nieco inaczej po druku.",
          "Na foliach przezroczystych i metalizowanych o wygląd grafiki decyduje biały poddruk. Zaznacz w pliku, które obszary mają być kryjące, a które mają pozostać przezroczyste lub odsłaniać srebrną albo złotą folię.",
        ],
      },
      {
        h2: "Typowe błędy w plikach",
        bullets: [
          "Grafika w zbyt małej rozdzielczości powiększona do rozmiaru naklejki.",
          "Bardzo cienkie linie i mikrotekst, które po zalaniu żywicą tracą czytelność.",
          "Brak zaznaczonej linii cięcia – kształt trzeba wtedy odtworzyć ręcznie.",
          "Kolory wyłącznie w RGB, bez podanego odpowiednika Pantone.",
          "Ważne elementy tuż przy krawędzi, bez marginesu bezpieczeństwa.",
        ],
      },
      {
        h2: "Co dzieje się po wysłaniu pliku",
        paragraphs: [
          "Każdy projekt sprawdzamy przed produkcją i przed realizacją wysyłamy go do akceptacji. Dopiero po Twoim potwierdzeniu uruchamiamy druk, wycinanie i zalewanie żywicą.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy mogę przysłać samo logo w JPG?",
        a: "Tak, ale jakość zależy od rozdzielczości. Plik wektorowy zawsze daje lepszy efekt, zwłaszcza przy małych naklejkach i cienkich elementach.",
      },
      {
        q: "Czy przygotujecie projekt za mnie?",
        a: "Przygotowujemy plik produkcyjny na podstawie dostarczonej grafiki: układ, spady i linię cięcia. Projektu graficznego od zera nie tworzymy.",
      },
      {
        q: "Czy zobaczę projekt przed produkcją?",
        a: "Tak. Przed realizacją zawsze wysyłamy projekt do akceptacji.",
      },
    ],
    links: [L.wypukle, L.zalewanie, L.kalkulator],
    related: ["wybor-folii-do-naklejek-3d", "jak-oszacowac-koszt-naklejek-3d"],
  },
  {
    slug: "wybor-folii-do-naklejek-3d",
    title: "Wybór folii do naklejek 3D – porównanie rodzajów",
    metaTitle: "Folie do naklejek 3D – jak wybrać rodzaj folii",
    metaDescription:
      "Porównanie folii do naklejek 3D: biała, transparentna, srebrna i złota chrom, szczotkowana, holograficzna. Jak wybrać folię do logo i podłoża.",
    excerpt:
      "Biała, transparentna, chromowana, szczotkowana czy holograficzna – jak rodzaj folii zmienia wygląd naklejki i do czego pasuje najlepiej.",
    date: "2026-09-09",
    category: "Materiały",
    lead:
      "Ta sama grafika na różnych foliach wygląda zupełnie inaczej. Folia decyduje o tym, czy naklejka będzie czysta i kontrastowa, metaliczna, czy efektowna i przyciągająca wzrok.",
    sections: [
      {
        h2: "Folia biała – uniwersalny wybór",
        paragraphs: [
          "Biała folia to najczęstszy wybór do logotypów firmowych. Daje najwierniejsze kolory i najlepszy kontrast, bo nadruk nie jest zakłócany przez kolor podłoża. Sprawdza się na każdym tle – jasnym i ciemnym.",
        ],
      },
      {
        h2: "Folia transparentna – naklejka wtapiająca się w podłoże",
        paragraphs: [
          "Folia przezroczysta pozwala uzyskać efekt grafiki „unoszącej się” nad powierzchnią. Wymaga jednak przemyślanego białego poddruku – bez niego kolory na ciemnym tle stracą intensywność. Dobrze działa na szkle, połyskliwych obudowach i lakierowanych panelach.",
        ],
      },
      {
        h2: "Folie metalizowane: chrom srebrny i złoty",
        paragraphs: [
          "Chrom srebrny i złoty nadają naklejce charakter emblematu. Nieprzedrukowane obszary zachowują metaliczny połysk, co daje efekt kojarzony z motoryzacją i produktami premium. Kolory nadrukowane na chromie są ciemniejsze niż na białej folii – warto to uwzględnić w projekcie.",
        ],
      },
      {
        h2: "Folia szczotkowana i holograficzna",
        paragraphs: [
          "Folia szczotkowana imituje szlifowane aluminium – dobrze wygląda na urządzeniach, panelach i tabliczkach technicznych. Folie holograficzne i z efektem szkła są najbardziej dekoracyjne, przyciągają uwagę na ekspozycji i w gadżetach reklamowych, ale są mniej odpowiednie tam, gdzie liczy się czytelność drobnego tekstu.",
        ],
      },
      {
        h2: "Jak wybrać folię – szybka podpowiedź",
        bullets: [
          "Logo firmowe, wierne kolory: folia biała.",
          "Efekt metalu, emblemat na sprzęcie lub aucie: chrom srebrny albo złoty.",
          "Grafika wtapiająca się w jasne podłoże: folia transparentna.",
          "Panel techniczny, wygląd aluminium: folia szczotkowana.",
          "Gadżet, ekspozycja, efekt „wow”: folia holograficzna lub z efektem szkła.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy cena zależy od rodzaju folii?",
        a: "Tak, folie specjalne są droższe od białej. Aktualne ceny dla wybranej folii pokazuje kalkulator wyceny.",
      },
      {
        q: "Czy mogę zobaczyć, jak wygląda dana folia?",
        a: "Tak – w kalkulatorze po kliknięciu w folię wyświetla się jej podgląd, a na stronie z realizacjami znajdziesz filmy z gotowymi naklejkami.",
      },
      {
        q: "Którą folię wybrać na ciemne podłoże?",
        a: "Najbezpieczniejsza jest folia biała lub transparentna z pełnym białym poddrukiem – kolory pozostaną wtedy nasycone.",
      },
    ],
    links: [L.wypukle, L.plaskie, L.realizacje],
    related: ["jak-przygotowac-plik-do-druku-naklejek-3d", "naklejki-3d-czy-naklejki-plaskie-porownanie"],
  },
  {
    slug: "trwalosc-naklejek-3d-w-warunkach-zewnetrznych",
    title: "Trwałość naklejek 3D w warunkach zewnętrznych",
    metaTitle: "Trwałość naklejek 3D – UV, woda, mróz i myjnie",
    metaDescription:
      "Jak długo wytrzymują naklejki 3D zalewane żywicą poliuretanową na zewnątrz: odporność na UV, wodę, mróz, upał, chemię i mycie ciśnieniowe.",
    excerpt:
      "Odporność na promieniowanie UV, wodę, mróz, upał i chemię – dlaczego żywica poliuretanowa wytrzymuje na zewnątrz wiele lat.",
    date: "2026-09-08",
    category: "Technologia",
    lead:
      "Naklejki 3D powstają z nadruku zalanego żywicą poliuretanową. Warstwa żywicy działa jak przezroczysta osłona – chroni grafikę przed wodą, promieniowaniem UV i uszkodzeniami mechanicznymi.",
    sections: [
      {
        h2: "Dlaczego żywica chroni nadruk",
        paragraphs: [
          "W klasycznej naklejce nadruk znajduje się na powierzchni i ściera się przy każdym kontakcie. W naklejce 3D nadruk jest zamknięty pod wypukłą warstwą żywicy, więc czynniki zewnętrzne działają na żywicę, a nie na grafikę.",
          "Żywica poliuretanowa jest przy tym elastyczna – nie pęka przy drganiach i lekkim odkształceniu podłoża, a z czasem nie żółknie tak jak tańsze powłoki.",
        ],
      },
      {
        h2: "Odporność na warunki atmosferyczne",
        bullets: [
          "Promieniowanie UV – nadruk pod żywicą zachowuje kolor przez lata ekspozycji na słońce.",
          "Woda, deszcz, wilgoć – naklejka jest wodoodporna, nie rozwarstwia się.",
          "Mróz i upał – szeroki zakres temperatur pracy, bez utraty elastyczności.",
          "Mycie ciśnieniowe i myjnie automatyczne – warstwa żywicy nie ulega zarysowaniu tak łatwo jak sam nadruk.",
        ],
      },
      {
        h2: "Co realnie skraca żywotność naklejki",
        paragraphs: [
          "Najczęstsza przyczyna problemów to nie materiał, a montaż: klejenie na brudnej, tłustej lub zbyt zimnej powierzchni. Drugi czynnik to agresywne rozpuszczalniki i szorstkie środki ścierne używane przy czyszczeniu.",
        ],
        bullets: [
          "Powierzchnia niedokładnie odtłuszczona przed klejeniem.",
          "Klejenie w temperaturze poniżej kilku stopni Celsjusza.",
          "Podłoże porowate, pylące lub świeżo malowane.",
          "Czyszczenie rozpuszczalnikami i pastami ściernymi.",
        ],
      },
      {
        h2: "Gdzie ta trwałość ma największe znaczenie",
        paragraphs: [
          "Najwięcej zyskują zastosowania zewnętrzne i przemysłowe: oznaczenia maszyn, tabliczki urządzeń, emblematy na pojazdach, elementy sprzętu pracującego w warsztacie czy na budowie. Przykłady z konkretnych branż zebraliśmy na osobnej stronie.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy naklejka 3D nadaje się na samochód?",
        a: "Tak. Warstwa żywicy jest odporna na wodę, UV i mycie, dlatego naklejki 3D stosuje się na karoserii, felgach i motocyklach.",
      },
      {
        q: "Czy naklejkę można umyć?",
        a: "Tak, wodą z łagodnym detergentem i miękką ściereczką. Unikaj rozpuszczalników i środków ściernych.",
      },
      {
        q: "Czy żywica pożółknie?",
        a: "Żywica poliuretanowa jest odporna na promieniowanie UV, dlatego zachowuje przezroczystość znacznie dłużej niż tańsze powłoki.",
      },
    ],
    links: [L.wypukle, L.identyfikatory, L.branze],
    related: ["montaz-naklejek-3d-krok-po-kroku", "naklejki-3d-w-motoryzacji-i-przemysle"],
  },
  {
    slug: "montaz-naklejek-3d-krok-po-kroku",
    title: "Montaż naklejek 3D krok po kroku",
    metaTitle: "Montaż naklejek 3D – instrukcja krok po kroku",
    metaDescription:
      "Jak prawidłowo nakleić naklejkę 3D: przygotowanie powierzchni, temperatura, pozycjonowanie, docisk i czas wiązania kleju. Instrukcja i typowe błędy.",
    excerpt:
      "Przygotowanie powierzchni, właściwa temperatura, docisk i czas wiązania kleju – instrukcja, dzięki której naklejka trzyma się od pierwszego razu.",
    date: "2026-09-07",
    category: "Montaż",
    lead:
      "Naklejki 3D mają mocny klej samoprzylepny, ale jego siła w pełni ujawnia się tylko na dobrze przygotowanym podłożu. Poniżej opisujemy cały montaż krok po kroku.",
    sections: [
      {
        h2: "Krok 1: przygotowanie powierzchni",
        paragraphs: [
          "Powierzchnia musi być czysta, sucha i odtłuszczona. Usuń pył i resztki starych klejów, a następnie przetrzyj miejsce klejenia alkoholem izopropylowym i poczekaj, aż całkowicie odparuje.",
        ],
      },
      {
        h2: "Krok 2: temperatura montażu",
        paragraphs: [
          "Klej najlepiej wiąże w temperaturze pokojowej. Unikaj klejenia na mrozie oraz na powierzchniach silnie nagrzanych słońcem. Jeśli element był na zewnątrz w zimie, pozwól mu ogrzać się w pomieszczeniu.",
        ],
      },
      {
        h2: "Krok 3: pozycjonowanie i docisk",
        paragraphs: [
          "Przy większych naklejkach warto wcześniej zaznaczyć położenie taśmą maskującą. Naklejkę przyklejaj od jednej krawędzi, stopniowo dociskając w stronę przeciwną – to zapobiega zamknięciu powietrza pod spodem.",
          "Na koniec dociśnij całą powierzchnię naklejki, zwracając szczególną uwagę na brzegi. Nie naciskaj punktowo w środek kopułki żywicy.",
        ],
      },
      {
        h2: "Krok 4: czas wiązania",
        paragraphs: [
          "Klej osiąga pełną siłę po pewnym czasie od montażu. W pierwszych godzinach nie moczy naklejki, nie myj powierzchni ciśnieniowo i nie narażaj jej na naprężenia. Dopiero po związaniu kleju naklejka jest w pełni gotowa na warunki zewnętrzne.",
        ],
      },
      {
        h2: "Najczęstsze błędy montażu",
        bullets: [
          "Klejenie na tłustej lub wilgotnej powierzchni.",
          "Montaż w niskiej temperaturze.",
          "Dotykanie warstwy kleju palcami.",
          "Zbyt szybkie mycie ciśnieniowe po naklejeniu.",
          "Odklejanie i ponowne przyklejanie tej samej naklejki – klej traci wtedy część siły.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy naklejkę 3D można zdjąć i przyklejć ponownie?",
        a: "Nie zalecamy tego. Po odklejeniu klej traci część siły wiązania, dlatego lepiej od razu ustalić dokładne położenie.",
      },
      {
        q: "Na jakich podłożach naklejka trzyma najlepiej?",
        a: "Na gładkich, nieporowatych powierzchniach: metalu, lakierze, szkle, tworzywach sztucznych i powlekanym drewnie.",
      },
      {
        q: "Czy naklejka nada się na powierzchnię zakrzywioną?",
        a: "Tak, żywica poliuretanowa jest elastyczna. Przy dużym promieniu krzywizny warto wybrać mniejszy rozmiar naklejki.",
      },
    ],
    links: [L.wypukle, L.identyfikatory, L.kalkulator],
    related: ["trwalosc-naklejek-3d-w-warunkach-zewnetrznych", "jak-przygotowac-plik-do-druku-naklejek-3d"],
  },
  {
    slug: "naklejki-3d-czy-naklejki-plaskie-porownanie",
    title: "Naklejki 3D czy naklejki płaskie – porównanie",
    metaTitle: "Naklejki 3D czy płaskie – porównanie i wybór",
    metaDescription:
      "Porównanie naklejek 3D zalewanych żywicą i naklejek płaskich: wygląd, trwałość, koszt i zastosowania. Sprawdź, który rodzaj wybrać dla firmy.",
    excerpt:
      "Wygląd, trwałość, koszt i zastosowania obu rodzajów naklejek – kiedy warto dopłacić za żywicę, a kiedy wystarczy wersja płaska.",
    date: "2026-09-06",
    category: "Poradnik zakupowy",
    lead:
      "Naklejka płaska i naklejka 3D powstają z tego samego nadruku. Różnica polega na warstwie żywicy, która zmienia zarówno wygląd, jak i odporność gotowego oznaczenia.",
    sections: [
      {
        h2: "Wygląd",
        paragraphs: [
          "Naklejka 3D ma wypukłą, przezroczystą kopułkę żywicy. Efekt soczewki pogłębia kolory i sprawia, że logo wygląda jak element wtopiony w produkt. Naklejka płaska jest równa z podłożem i wygląda dokładnie tak, jak wydruk.",
        ],
      },
      {
        h2: "Trwałość",
        paragraphs: [
          "W naklejce 3D nadruk jest zamknięty pod żywicą, więc nie ściera się i nie blaknie tak szybko. Naklejka płaska ma nadruk zabezpieczony wyłącznie laminatem lub lakierem, dlatego przy intensywnym użytkowaniu na zewnątrz zużywa się szybciej.",
        ],
      },
      {
        h2: "Koszt i nakład",
        paragraphs: [
          "Naklejki płaskie są tańsze, bo pomijają etap zalewania i utwardzania żywicy. Przy dużych nakładach materiałów informacyjnych czy oznaczeń jednorazowych to zwykle wystarczające rozwiązanie. Naklejki 3D traktuj jako element wizerunkowy – tam liczy się efekt i trwałość, a nie najniższa cena sztuki.",
        ],
      },
      {
        h2: "Kiedy co wybrać",
        bullets: [
          "Naklejki 3D: logo na produkcie, emblematy, oznaczenia urządzeń, elementy premium, zastosowania zewnętrzne.",
          "Naklejki płaskie: etykiety informacyjne, oznaczenia opakowań, duże nakłady promocyjne, krótki okres użytkowania.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy mogę zamówić ten sam projekt w obu wersjach?",
        a: "Tak. Ten sam plik wykorzystujemy zarówno do naklejek płaskich, jak i do wersji zalewanej żywicą.",
      },
      {
        q: "Czy naklejki płaskie też są wodoodporne?",
        a: "Są odporne na wilgoć, ale ich nadruk nie jest chroniony warstwą żywicy, dlatego przy intensywnym użytkowaniu zewnętrznym wytrzymują krócej.",
      },
      {
        q: "Jak porównać koszt obu wersji?",
        a: "Najprościej w kalkulatorze – wystarczy porównać cenę dla tych samych wymiarów i nakładu.",
      },
    ],
    links: [L.wypukle, L.plaskie, L.kalkulator],
    related: ["jak-oszacowac-koszt-naklejek-3d", "wybor-folii-do-naklejek-3d"],
  },
  {
    slug: "jak-oszacowac-koszt-naklejek-3d",
    title: "Jak oszacować koszt naklejek 3D",
    metaTitle: "Koszt naklejek 3D – od czego zależy cena",
    metaDescription:
      "Od czego zależy cena naklejek 3D: wymiary, nakład, rodzaj folii i kształt. Jak samodzielnie oszacować koszt zamówienia w kalkulatorze wyceny.",
    excerpt:
      "Wymiary, nakład, folia i kształt – cztery czynniki, które decydują o cenie, oraz jak policzyć koszt zamówienia samodzielnie.",
    date: "2026-09-05",
    category: "Poradnik zakupowy",
    lead:
      "Cena naklejek 3D nie jest ryczałtowa – wynika z powierzchni jednej naklejki, liczby sztuk, rodzaju folii i złożoności kształtu. Wszystkie te parametry ustawisz w kalkulatorze i od razu zobaczysz wynik.",
    sections: [
      {
        h2: "Wymiary naklejki",
        paragraphs: [
          "Podstawą wyceny jest powierzchnia. Im większa naklejka, tym więcej folii i żywicy zużywamy na sztukę. Realizujemy formaty od 5 × 5 mm do 380 × 280 mm.",
        ],
      },
      {
        h2: "Nakład",
        paragraphs: [
          "Nakład ma największy wpływ na cenę jednostkową. Koszty przygotowania produkcji rozkładają się na wszystkie sztuki, więc przy większych zamówieniach cena za sztukę wyraźnie spada. Jeśli planujesz kilka partii w roku, zwykle taniej wyjdzie jedno większe zamówienie.",
        ],
      },
      {
        h2: "Folia i kształt",
        paragraphs: [
          "Folie specjalne – chromowane, szczotkowane, holograficzne – są droższe od białej. Kształty standardowe (prostokąt, koło, owal) są tańsze w wycinaniu niż wykrój po skomplikowanym obrysie logo.",
        ],
      },
      {
        h2: "Minimum zamówienia i wysyłka",
        paragraphs: [
          "Minimum logistyczne, jakie realizujemy, zaczyna się od kwoty 100 zł netto. Do oferty doliczana jest wysyłka kurierem w wysokości 18 zł netto. Przy sprzedaży dla firm z Unii Europejskiej stosujemy stawkę VAT 0%.",
        ],
      },
      {
        h2: "Jak policzyć koszt samodzielnie",
        bullets: [
          "Wpisz szerokość i wysokość naklejki w milimetrach.",
          "Podaj liczbę sztuk – sprawdź kilka progów nakładu.",
          "Wybierz folię i kształt.",
          "Odczytaj cenę netto w złotówkach i euro, a następnie dodaj koszt wysyłki.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy mogę dostać ofertę w PDF?",
        a: "Tak. W kalkulatorze możesz zapisać skonfigurowaną wycenę do pliku PDF razem z kosztem wysyłki.",
      },
      {
        q: "Czy cena zależy od liczby kolorów?",
        a: "Nie. Drukujemy pełnokolorowo, więc liczba kolorów w projekcie nie zmienia ceny.",
      },
      {
        q: "Jak wygląda wycena usługi na materiale klienta?",
        a: "Zalewanie żywicą na Twoim materiale wyceniamy indywidualnie – zależy od kształtu elementu i powierzchni zalewania.",
      },
    ],
    links: [L.kalkulator, L.wypukle, L.zalewanie],
    related: ["naklejki-3d-czy-naklejki-plaskie-porownanie", "jak-wyglada-zamowienie-naklejek-3d-dla-firmy"],
  },
  {
    slug: "naklejki-3d-w-motoryzacji-i-przemysle",
    title: "Naklejki 3D w motoryzacji i przemyśle",
    metaTitle: "Naklejki 3D w motoryzacji i przemyśle – zastosowania",
    metaDescription:
      "Zastosowania naklejek 3D w motoryzacji i przemyśle: emblematy, oznaczenia maszyn, panele operatorskie, tabliczki urządzeń. Wymagania i dobór materiału.",
    excerpt:
      "Emblematy na pojazdach, oznaczenia maszyn i panele operatorskie – gdzie naklejki 3D zastępują tabliczki i oznaczenia malowane.",
    date: "2026-09-04",
    category: "Zastosowania",
    lead:
      "Motoryzacja i przemysł to dwa obszary, w których naklejki 3D pracują w najtrudniejszych warunkach: wibracje, oleje, mycie ciśnieniowe, słońce i mróz.",
    sections: [
      {
        h2: "Motoryzacja: emblematy i oznaczenia pojazdów",
        paragraphs: [
          "Naklejki 3D stosuje się jako emblematy na karoserii, oznaczenia felg, elementy tuningowe, logotypy na motocyklach i naklejki flotowe. Wypukła żywica daje efekt odlewanego elementu, a odporność na UV i mycie sprawia, że oznaczenie długo wygląda jak nowe.",
        ],
        bullets: [
          "Emblematy i logotypy na karoserii oraz klapach.",
          "Oznaczenia felg i elementów tuningowych.",
          "Naklejki flotowe z nazwą firmy i numerem pojazdu.",
          "Elementy wnętrza: panele, przełączniki, oznaczenia funkcji.",
        ],
      },
      {
        h2: "Przemysł: maszyny, urządzenia i panele",
        paragraphs: [
          "W zakładach produkcyjnych naklejki 3D pełnią funkcję trwałych oznaczeń: logotypy producenta na obudowach maszyn, opisy paneli operatorskich, oznaczenia funkcji, numery seryjne i tabliczki urządzeń. Żywica chroni nadruk przed olejami, smarami i codziennym czyszczeniem.",
        ],
      },
      {
        h2: "Dobór materiału do tych zastosowań",
        bullets: [
          "Folia biała – najlepszy kontrast opisów technicznych.",
          "Chrom srebrny – wygląd metalowego emblematu.",
          "Folia szczotkowana – imitacja szlifowanego aluminium na panelach.",
          "Prostszy kształt – mniejsze ryzyko podrywania krawędzi przy myciu.",
        ],
      },
      {
        h2: "Kiedy zamiast naklejki lepszy jest identyfikator",
        paragraphs: [
          "Jeśli oznaczenie musi zawierać dużo danych technicznych, numer seryjny albo pełnić funkcję tabliczki znamionowej, lepszym rozwiązaniem jest identyfikator 3D. Gdy masz już własny nadruk, wykonamy wyłącznie usługę zalewania żywicą.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy naklejka 3D wytrzyma mycie ciśnieniowe?",
        a: "Tak, po pełnym związaniu kleju. Warstwa żywicy chroni nadruk przed wodą i zarysowaniem.",
      },
      {
        q: "Czy naklejki nadają się na oleje i smary?",
        a: "Żywica poliuretanowa jest odporna na kontakt z typowymi olejami i smarami technicznymi. Unikaj agresywnych rozpuszczalników.",
      },
      {
        q: "Czy wykonacie oznaczenia z numeracją?",
        a: "Tak, przy oznaczeniach z numerami seryjnymi prosimy o kontakt – szczegóły ustalamy indywidualnie.",
      },
    ],
    links: [L.wypukle, L.identyfikatory, L.branze],
    related: ["trwalosc-naklejek-3d-w-warunkach-zewnetrznych", "jak-wyglada-zamowienie-naklejek-3d-dla-firmy"],
  },
  {
    slug: "jak-wyglada-zamowienie-naklejek-3d-dla-firmy",
    title: "Jak wygląda zamówienie naklejek 3D dla firmy",
    metaTitle: "Zamówienie naklejek 3D dla firmy – proces B2B",
    metaDescription:
      "Proces zamówienia naklejek 3D dla firm: wycena online, przesłanie pliku, akceptacja projektu, produkcja i wysyłka kurierem. Minimum i warunki B2B.",
    excerpt:
      "Od wyceny online do wysyłki kurierem – cały proces zamówienia B2B, wraz z minimum logistycznym i akceptacją projektu.",
    date: "2026-09-03",
    category: "Zamówienie",
    lead:
      "Zamówienie naklejek 3D dla firmy przebiega w kilku prostych etapach. Poniżej opisujemy, co dzieje się na każdym z nich i co jest potrzebne z Twojej strony.",
    sections: [
      {
        h2: "Etap 1: wycena online",
        paragraphs: [
          "W kalkulatorze podajesz wymiary, nakład, folię i kształt, a cenę netto widzisz od razu – w złotówkach i euro. Możesz dodać kilka pozycji do jednego zapytania i zapisać całość jako ofertę PDF.",
        ],
      },
      {
        h2: "Etap 2: przesłanie projektu",
        paragraphs: [
          "Do zapytania dołączasz plik z grafiką. Najlepiej w formacie wektorowym; wymagania techniczne opisaliśmy w osobnym artykule. Jeśli wyślesz zapytanie bez projektu, poprosimy o jego uzupełnienie.",
        ],
      },
      {
        h2: "Etap 3: akceptacja projektu",
        paragraphs: [
          "Przygotowujemy plik produkcyjny i wysyłamy go do akceptacji. To moment na korekty układu, rozmiaru i kolorów. Produkcję uruchamiamy dopiero po Twoim potwierdzeniu.",
        ],
      },
      {
        h2: "Etap 4: produkcja i wysyłka",
        paragraphs: [
          "Po akceptacji następuje druk, precyzyjne wycinanie i zalewanie żywicą poliuretanową, a następnie utwardzanie. Gotowe naklejki wysyłamy kurierem – koszt wysyłki to 18 zł netto.",
        ],
      },
      {
        h2: "Warunki współpracy B2B",
        bullets: [
          "Minimum logistyczne zamówienia: od 100 zł netto.",
          "Sprzedaż dla firm z Unii Europejskiej: stawka VAT 0%.",
          "Wysyłka kurierem: 18 zł netto.",
          "Powtarzalne zamówienia realizujemy na podstawie zatwierdzonego wcześniej projektu.",
        ],
      },
    ],
    faqs: [
      {
        q: "Czy dostanę fakturę?",
        a: "Tak, realizujemy zamówienia dla firm i wystawiamy fakturę.",
      },
      {
        q: "Czy mogę zamówić ponownie ten sam wzór?",
        a: "Tak. Przy powtórnym zamówieniu wystarczy podać wcześniej zatwierdzony projekt – nie trzeba przechodzić przez etap akceptacji od nowa.",
      },
      {
        q: "Czy wysyłacie zamówienia za granicę?",
        a: "Tak, realizujemy zamówienia dla firm z Unii Europejskiej. Warunki wysyłki poza Polskę ustalamy indywidualnie.",
      },
    ],
    links: [L.kalkulator, L.wypukle, L.branze],
    related: ["jak-oszacowac-koszt-naklejek-3d", "jak-przygotowac-plik-do-druku-naklejek-3d"],
  },
];

export const getKnowledgeArticle = (slug: string) =>
  KNOWLEDGE_ARTICLES.find((a) => a.slug === slug);

export const getRelatedKnowledge = (article: KnowledgeArticle) =>
  article.related
    .map((slug) => getKnowledgeArticle(slug))
    .filter((a): a is KnowledgeArticle => Boolean(a));
